import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserMenu } from '@/components/common/UserMenu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMeuCadastroPrestador } from '@/hooks/usePrestadores';
import { useAgendaOS } from '@/hooks/useAgenda';
import { filtrarMinhasOS } from '@/lib/minha-agenda';
import { getWeekRange, getDayRange } from '@/lib/agenda-timeline';
import { AgendaToolbar } from '@/components/agenda/AgendaToolbar';
import { WeekCalendarView } from '@/components/agenda/WeekCalendarView';
import { MinhaOSDetalheDialog } from '@/components/agenda/MinhaOSDetalheDialog';
import { type AgendaView } from '@/components/agenda/TimelineGrid';
import type { AgendaOS } from '@/services/agenda.service';

export default function MinhaAgenda() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [viewManual, setViewManual] = useState<AgendaView | null>(null);
  const view: AgendaView = viewManual ?? (isMobile ? 'day' : 'week');
  const [viewDate, setViewDate] = useState<Date>(() => new Date());
  const [osSelecionada, setOsSelecionada] = useState<AgendaOS | null>(null);

  const range = useMemo(
    () => (view === 'week' ? getWeekRange(viewDate) : getDayRange(viewDate)),
    [view, viewDate],
  );

  const { data: meuCadastro } = useMeuCadastroPrestador();
  const { data: agendaOrdens, isLoading } = useAgendaOS(range);

  const ordens = useMemo(
    () => filtrarMinhasOS(agendaOrdens ?? [], meuCadastro?.id ?? null),
    [agendaOrdens, meuCadastro?.id],
  );

  const handleOSClick = (osId: string) => {
    const found = ordens.find((o) => o.id === osId) ?? null;
    setOsSelecionada(found);
  };

  return (
    <MainLayout>
      <div className="flex h-full flex-col p-6 gap-4">
        <PageHeader titulo={t('nav.agenda')} acoes={<UserMenu />} />

        <AgendaToolbar
          view={view}
          viewDate={viewDate}
          onViewChange={(v) => setViewManual(v)}
          onDateChange={setViewDate}
        />

        <div className="flex flex-1 overflow-hidden rounded-2xl border border-border-soft bg-card shadow-sm">
          {isLoading ? (
            <div className="flex-1 animate-pulse bg-muted/20" />
          ) : (
            <WeekCalendarView
              view={view}
              viewDate={viewDate}
              prestadores={[]}
              ordens={ordens}
              onOSClick={handleOSClick}
              readOnly
            />
          )}
        </div>
      </div>

      <MinhaOSDetalheDialog
        os={osSelecionada}
        open={osSelecionada !== null}
        onOpenChange={(o) => !o && setOsSelecionada(null)}
      />
    </MainLayout>
  );
}
